import Button from "@imminently/decisively-ui/Button";
import { type ToolbarButtonProps as PlateToolbarButtonProps } from "@udecode/plate";
import { useCallback, useReducer } from "react";
import {
  type PlateEditor,
  type ToggleMarkPlugin,
  type Value,
  type WithPlatePlugin,
  someNode,
  toggleMark,
  toggleNodeType,
  useEventPlateId,
  usePlateEditorState,
  usePlateSelectors,
} from "@udecode/plate-core";
import { usePublishToolbarState, useToolbarBlockState, useToolbarEditor, useToolbarMarkState } from "./toolbarState";

// Custom ToolbarButton implementation
// PlateJS docs and discussions suggest creating a custom ToolbarButton as theres is just an example
// based off their own implementation:
// https://github.com/udecode/plate/blob/main/packages/ui/toolbar/src/ToolbarButton/ToolbarButton.tsx

interface ToolbarButtonProps extends Omit<PlateToolbarButtonProps, "tooltip"> {
  /* Disabled state or function to determine state */
  disable?: boolean | ((editor: any) => boolean);
  /* Tooltip text */
  tooltip?: string;
  /* Optional keyboard shortcut hints rendered in tooltip */
  hotkeys?: string[];
  /* Hide button if disabled */
  hideDisabled?: boolean;
  /* List of allowed elements based on selection. Will disable if element is not in the list. */
  allow?: string[];
}

const useToolbarCommandRefresh = () => {
  const [, forceRender] = useReducer((count) => count + 1, 0);

  return useCallback(() => {
    forceRender();
    requestAnimationFrame(forceRender);
  }, []);
};

/**
 * Custom ToolbarButton using MUI Button.
 * Adds support for easy tooltips, disabled state, hiding on disabled and allowing only certain elements.
 */

export const ToolbarButton = (props: ToolbarButtonProps) => {
  const { id, icon, tooltip, hotkeys, hideDisabled, disable, active, allow, onMouseDown, ...rest } = props;
  const plateId = useEventPlateId(id);
  usePlateSelectors(plateId).keySelection();
  const editor = usePlateEditorState(plateId);

  // if disable is not provided, default to false
  let disabled = disable ?? false;
  // allow disable to be a function
  if (disable instanceof Function) {
    disabled = editor && disable(editor);
  }

  // if allow is provided, check they are on a valid node
  // only check if not already disabled
  if (disabled === false && allow) {
    disabled = !!editor?.selection && someNode(editor, { match: { type: allow } });
  }

  //console.log('[ToolbarButton] disabled', tooltip, disable, disabled, active);

  if (hideDisabled && disabled) return null;

  const buttonProps = {
    onMouseDown,
    disabled: disabled as boolean,
    ...rest,
  };

  const button = (
    <Button icon
      tooltip={tooltip ?? false}
      hotkeys={hotkeys}
      selected={active}
      data-testid="ToolbarButton"
      label={tooltip ?? "Toolbar action"}
      className="aspect-square h-8 p-0"
      {...buttonProps}
    >
      {icon}
    </Button>
  );
  return button;
};

export interface EditorToolbarButtonProps extends ToolbarButtonProps {
  onClick?: (editor: PlateEditor) => void;
}

/**
 * Toolbar button which passes the editor instance to the onClick handler.
 */
export const EditorToolbarButton = ({ id, onClick, active, ...props }: EditorToolbarButtonProps) => {
  const editor = useToolbarEditor(id);
  const refreshToolbarState = useToolbarCommandRefresh();
  const publishToolbarState = usePublishToolbarState(id);

  return (
    <ToolbarButton
      id={id}
      active={typeof active === "function" ? active(editor) : active}
      onMouseDown={
        editor &&
        ((event) => {
          event.preventDefault();
          onClick?.(editor);
          publishToolbarState(editor);
          refreshToolbarState();
        })
      }
      {...props}
    />
  );
};

// See: https://github.com/udecode/plate/blob/main/packages/ui/toolbar/src/BlockToolbarButton/BlockToolbarButton.tsx

export interface BlockToolbarButtonProps extends ToolbarButtonProps {
  type: string;
  inactiveType?: string;
  toggle?: (editor: any, options: any, editorNodesOptions?: any) => void;
}

/**
 * Toolbar button to toggle the type of elements in selection.
 */
export const BlockToolbarButton = ({ id, type, inactiveType, active, toggle, ...props }: BlockToolbarButtonProps) => {
  const { editor, active: subscribedActive } = useToolbarBlockState(id, type);
  const refreshToolbarState = useToolbarCommandRefresh();
  const publishToolbarState = usePublishToolbarState(id);
  // if(toggle) { debugger; }
  const onClick = toggle ?? toggleNodeType;
  return (
    <ToolbarButton
      id={id}
      active={(typeof active === "function" ? active(editor) : active) ?? subscribedActive}
      onMouseDown={
        editor &&
        ((event) => {
          event.preventDefault();
          onClick(editor, {
            activeType: type,
            inactiveType,
          });
          publishToolbarState(editor);
          refreshToolbarState();
        })
      }
      {...props}
    />
  );
};

// See: https://github.com/udecode/plate/blob/main/packages/ui/toolbar/src/MarkToolbarButton/MarkToolbarButton.types.ts

export interface MarkToolbarButtonProps<V extends Value>
  extends ToolbarButtonProps,
    Pick<WithPlatePlugin<V>, "type">,
    Pick<ToggleMarkPlugin, "clear"> {}

/**
 * Toolbar button to toggle the mark of the leaves in selection.
 */
export const MarkToolbarButton = <V extends Value>({ id, type, clear, active, ...props }: MarkToolbarButtonProps<V>) => {
  const { editor, active: subscribedActive } = useToolbarMarkState(id, type);
  const refreshToolbarState = useToolbarCommandRefresh();
  const publishToolbarState = usePublishToolbarState(id);

  return (
    <ToolbarButton
      id={id}
      active={(typeof active === "function" ? active(editor) : active) ?? subscribedActive}
      onMouseDown={
        editor
          ? (event) => {
            event.preventDefault();
            toggleMark(editor, { key: type, clear });
            publishToolbarState(editor);
            refreshToolbarState();
          }
        : undefined
      }
      {...props}
    />
  );
};
